import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Download, Upload, CheckCircle2, XCircle, FileSpreadsheet,
  ArrowLeft, AlertTriangle, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import {
  buildBulkInventoryTemplate, parseBulkInventoryFile, validateInventoryRowsClient,
} from "@/lib/bulkInventoryVouchers";

const VTYPE_BADGE = {
  Purchase: "bg-orange-50 text-orange-700",
  Sales: "bg-emerald-50 text-emerald-700",
  "Debit Note": "bg-amber-50 text-amber-700",
  "Credit Note": "bg-cyan-50 text-cyan-700",
};

function Inner() {
  const { active, activeId } = useCompany();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [ledgers, setLedgers] = useState([]);
  const [items, setItems] = useState([]);
  const [autoCreate, setAutoCreate] = useState(true);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!activeId) return;
    Promise.all([
      api.get(`/companies/${activeId}/ledgers`),
      api.get(`/companies/${activeId}/items`),
    ]).then(([L, I]) => { setLedgers(L.data); setItems(I.data); });
  }, [activeId]);

  const ledgerNames = useMemo(() => ledgers.map((l) => l.name), [ledgers]);
  const itemNames = useMemo(() => items.map((i) => i.name), [items]);
  const validRows = useMemo(() => (preview || []).filter((r) => r.issues.length === 0), [preview]);
  const invalidRows = useMemo(() => (preview || []).filter((r) => r.issues.length > 0), [preview]);

  const onCreate = async () => {
    if (!active) return;
    setBusy(true);
    try {
      await buildBulkInventoryTemplate(ledgerNames, itemNames, active.name);
      toast.success("Template downloaded — open it in Excel and fill rows.");
    } catch (err) {
      toast.error(`Failed to generate template: ${err?.message || err}`);
    } finally {
      setBusy(false);
    }
  };

  const onPick = () => fileRef.current?.click();

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const parsed = await parseBulkInventoryFile(file);
      if (!parsed.length) {
        toast.error("No data rows found in the uploaded file.");
        setPreview(null);
        return;
      }
      const validated = validateInventoryRowsClient(parsed, ledgerNames, itemNames, autoCreate);
      setPreview(validated);
      const okCount = validated.filter((r) => r.issues.length === 0).length;
      toast.success(`${parsed.length} rows parsed — ${okCount} look good, ${parsed.length - okCount} need attention`);
    } catch (err) {
      toast.error(err.message || "Could not parse file");
      setPreview(null);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onImport = async () => {
    if (!validRows.length) return;
    setBusy(true);
    try {
      const payload = validRows.map(({ issues, excel_row, ...row }) => row);
      const { data } = await api.post(`/companies/${activeId}/vouchers/bulk-inventory`, {
        rows: payload, auto_create: autoCreate,
      });
      // Map server results to original row numbers via order
      setResult(data);
      setPreview(null);
      if (data.created > 0) toast.success(`${data.created} voucher${data.created === 1 ? "" : "s"} created`);
      if (data.failed > 0) toast.error(`${data.failed} row${data.failed === 1 ? "" : "s"} failed`);
      // Refresh ledger + item lists since auto-create may have added some
      const [L, I] = await Promise.all([
        api.get(`/companies/${activeId}/ledgers`),
        api.get(`/companies/${activeId}/items`),
      ]);
      setLedgers(L.data); setItems(I.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Upload Bulk Purchase Vouchers"
        subtitle="Multi-line Purchase / Sales / Debit Note / Credit Note import from Excel"
        actions={
          <Button variant="outline" className="h-8" onClick={() => navigate("/vouchers")} data-testid="btn-back-vouchers">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Vouchers
          </Button>
        }
      />
      <div className="p-6 space-y-4 max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <section className="border border-gray-200 rounded bg-white p-5">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded bg-orange-50 text-orange-700 flex items-center justify-center">
                <Download className="w-4 h-4" />
              </div>
              <h2 className="font-semibold text-gray-900">Create new excel file</h2>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Generates a template with <strong>16 columns</strong> including HSN, qty, rate, <strong>discount %</strong>, GST and a voucher-type dropdown.
              Rows sharing the same <strong>Voucher No</strong> + Date + Party are merged into one multi-line voucher.
            </p>
            <Button onClick={onCreate} disabled={busy} className="bg-orange-700 hover:bg-orange-800 h-9" data-testid="btn-create-purchase-template">
              <FileSpreadsheet className="w-4 h-4 mr-1.5" />
              {busy ? "Generating…" : "Create new excel file"}
            </Button>
          </section>

          <section className="border border-gray-200 rounded bg-white p-5">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded bg-emerald-50 text-emerald-700 flex items-center justify-center">
                <Upload className="w-4 h-4" />
              </div>
              <h2 className="font-semibold text-gray-900">Upload excel file</h2>
            </div>
            <p className="text-sm text-gray-500 mb-3">
              Upload the filled template. We&apos;ll show a preview before any vouchers are created.
            </p>
            <label className="flex items-center gap-2 text-sm mb-3 text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={autoCreate}
                onChange={(e) => setAutoCreate(e.target.checked)}
                data-testid="check-auto-create"
              />
              <span>
                Auto-create missing party ledgers (Sundry Creditors/Debtors) &amp; items
                <span className="text-xs text-gray-500 ml-1">(recommended)</span>
              </span>
            </label>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={onUpload} className="hidden" data-testid="input-upload-purchase" />
            <Button onClick={onPick} disabled={busy} variant="outline" className="h-9" data-testid="btn-upload-purchase">
              <Upload className="w-4 h-4 mr-1.5" />
              {busy ? "Processing…" : "Upload excel file"}
            </Button>
          </section>
        </div>

        <section className="border border-gray-200 rounded bg-gray-50 p-4 text-xs text-gray-600 space-y-1">
          <p className="font-semibold text-gray-700 mb-1">Template columns (16):</p>
          <p className="font-mono text-[11px]">
            Date · Voucher No (optional) · Reference · Party Ledger · Item · HSN/SAC · Qty · Unit · Rate · <strong>Disc %</strong> · Amount · CGST · SGST · IGST · Total · Voucher Type
          </p>
          <ul className="list-disc list-inside space-y-0.5 mt-2">
            <li><strong>Multi-line vouchers:</strong> rows that share the same Voucher No + Date + Party become one voucher with multiple line items.</li>
            <li>Leave <em>Voucher No</em> blank to have each row become its own voucher.</li>
            <li>If <strong>Amount</strong> is blank we compute it as Qty × Rate; if <strong>Total</strong> is blank we compute Amount + CGST + SGST + IGST.</li>
            <li>Default Voucher Type is <strong>Purchase</strong>; you can also pick Sales / Debit Note / Credit Note per row.</li>
            <li>Ledger postings (Party / Purchase or Sales A/c / Input or Output GST) are generated automatically — visible in Trial Balance, P&amp;L, Balance Sheet.</li>
          </ul>
        </section>

        {preview && (
          <section className="border border-gray-200 rounded bg-white overflow-hidden" data-testid="preview-section">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center gap-3">
              <div className="text-xs uppercase tracking-wider text-gray-600 font-semibold">Preview before import</div>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> {validRows.length} ready</span>
                {invalidRows.length > 0 && (
                  <span className="text-amber-700 inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> {invalidRows.length} skipped</span>
                )}
              </div>
              <div className="ml-auto flex items-center gap-2">
                <Button variant="outline" className="h-8 text-xs" onClick={() => setPreview(null)} data-testid="btn-cancel-preview">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Discard
                </Button>
                <Button className="bg-orange-700 hover:bg-orange-800 h-8 text-xs" onClick={onImport} disabled={busy || !validRows.length} data-testid="btn-confirm-import">
                  Import {validRows.length} row{validRows.length === 1 ? "" : "s"}
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="tx-table min-w-[1100px]">
                <thead>
                  <tr>
                    <th>Row</th><th>Date</th><th>Voucher No</th><th>Type</th><th>Party</th><th>Item</th>
                    <th className="text-right">Qty</th><th className="text-right">Rate</th>
                    <th className="text-right">Disc %</th>
                    <th className="text-right">Amount</th><th className="text-right">GST</th>
                    <th className="text-right">Total</th><th>Status</th>
                  </tr>
                </thead>
                <tbody data-testid="preview-rows">
                  {preview.map((r) => (
                    <tr key={r.excel_row} className={r.issues.length ? "bg-amber-50/40" : ""}>
                      <td className="font-mono text-xs">{r.excel_row}</td>
                      <td className="font-mono text-xs">{r.date || <span className="text-red-600">—</span>}</td>
                      <td className="font-mono text-xs">{r.voucher_no || "—"}</td>
                      <td><span className={`text-xs px-1.5 py-0.5 rounded ${VTYPE_BADGE[r.voucher_type] || "bg-gray-100 text-gray-700"}`}>{r.voucher_type}</span></td>
                      <td className="text-xs">{r.party_ledger || <span className="text-red-600 italic">missing</span>}</td>
                      <td className="text-xs">{r.item_name || <span className="text-red-600 italic">missing</span>}</td>
                      <td className="tx-num">{formatINR(r.qty)}</td>
                      <td className="tx-num">{formatINR(r.rate)}</td>
                      <td className="tx-num">{r.discount_pct ? `${r.discount_pct}%` : "—"}</td>
                      <td className="tx-num">{formatINR(r.amount)}</td>
                      <td className="tx-num">{formatINR(r.cgst + r.sgst + r.igst)}</td>
                      <td className="tx-num">{formatINR(r.total)}</td>
                      <td>
                        {r.issues.length === 0 ? (
                          <span className="text-emerald-700 inline-flex items-center gap-1 text-xs"><CheckCircle2 className="w-3.5 h-3.5" /> Ready</span>
                        ) : (
                          <span className="text-amber-700 inline-flex items-center gap-1 text-xs" title={r.issues.join("; ")}>
                            <AlertTriangle className="w-3.5 h-3.5" />
                            {r.issues[0]}{r.issues.length > 1 ? ` (+${r.issues.length - 1})` : ""}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {result && (
          <section className="border border-gray-200 rounded bg-white overflow-hidden" data-testid="result-section">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center gap-3">
              <div className="text-xs uppercase tracking-wider text-gray-600 font-semibold">Import result</div>
              <span className="text-emerald-700 inline-flex items-center gap-1 text-xs"><CheckCircle2 className="w-3.5 h-3.5" /> {result.created} vouchers created</span>
              <span className="text-red-700 inline-flex items-center gap-1 text-xs"><XCircle className="w-3.5 h-3.5" /> {result.failed} rows failed</span>
              <span className="text-gray-500 text-xs ml-2">{result.total_rows} rows processed</span>
            </div>
            <table className="tx-table">
              <thead><tr><th>Row</th><th>Status</th><th>Voucher / Error</th></tr></thead>
              <tbody data-testid="bulk-result-rows">
                {result.results.map((r, i) => (
                  <tr key={`${r.row}-${i}`}>
                    <td className="font-mono">{r.row}</td>
                    <td>
                      {r.ok ? (
                        <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> OK</span>
                      ) : (
                        <span className="text-red-700 inline-flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> Error</span>
                      )}
                    </td>
                    <td className="font-mono text-xs">
                      {r.ok ? `${r.voucher_no} (${r.voucher_type})` : <span className="text-red-700">{r.error}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </div>
    </>
  );
}

export default function BulkPurchaseVouchers() {
  return <RequireCompany><Inner /></RequireCompany>;
}
